/**
 * Página de Gerenciamento de Usuários do Sistema MEDICARE
 * Permite visualizar, criar, editar e excluir usuários
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Badge,
  Avatar,
  useDisclosure,
  useColorModeValue,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useToast,
  Spinner,
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Switch,
  Textarea,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiRefreshCw,
  FiFilter,
  FiDownload,
  FiList,
} from 'react-icons/fi';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { UsuariosService } from '../../services/usuarios';
import { EndpointsService, Endpoint } from '../../services/endpoints';
import { PromptsService, Prompt } from '../../services/prompts';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useNotifications } from '../../contexts/ToastContext';
import { getAvatarUrl } from '../../utils/avatarUtils';

interface User {
  id: string;
  nome: string;
  email: string;
  tipo: 'admin' | 'medico';
  ativo: boolean;
  criadoEm: string;
  ultimoLogin?: string;
  avatar?: string;
  endpoint_id?: string;
  prompt_ia?: string;
  senha?: string;
}

const USER_TYPES = {
  admin: { label: 'Administrador', color: 'purple' },
  medico: { label: 'Médico', color: 'blue' },
};

export const UserManagementPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState<Partial<User>>({});
  const [promptSelectionMode, setPromptSelectionMode] = useState<'edit' | 'create'>('edit');
  
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isPromptsOpen, onOpen: onPromptsOpen, onClose: onPromptsClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  
  const toast = useToast();
  const { showSuccess, showError } = useNotifications();
  const queryClient = useQueryClient();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Função para validar senha
  const isValidPassword = (password: string): boolean => {
    if (!password || password.length < 8) return false;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    return hasUpperCase && hasLowerCase && hasNumber;
  };



  // Query para buscar endpoints
  const {
    data: endpoints = [],
    isLoading: isLoadingEndpoints,
    error: endpointsError,
  } = useQuery<Endpoint[]>('endpoints', async () => {
    try {
      const response = await EndpointsService.listar(1, 100, { ativo: true });
      return response.data;
    } catch (error) {
      // Se falhar por autenticação, retorna array vazio em vez de falhar
      console.warn('Não foi possível carregar endpoints:', error);
      return [];
    }
  }, {
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Query para buscar prompts
  const {
    data: prompts = [],
    isLoading: isLoadingPrompts,
  } = useQuery<Prompt[]>('prompts-list', async () => {
    try {
      const response = await PromptsService.listar({ 
        ativo: true,
        limit: 100 // Buscar todos os prompts ativos
      });
      // Mapear dados da API para o formato esperado pelo frontend
      const promptsMapeados = (response.dados?.prompts || []).map((prompt: any) => ({
        ...prompt,
        // Campos de compatibilidade
        descricao: prompt.parametros?.descricao || '',
        conteudo: prompt.prompt_texto, // Mapear prompt_texto para conteudo
        variaveis: prompt.parametros?.variaveis || [],
        temperatura: prompt.parametros?.temperatura || 0.7,
        maxTokens: prompt.parametros?.maxTokens || 1500,
      }));
      return promptsMapeados;
    } catch (error) {
      console.warn('Não foi possível carregar prompts:', error);
      return [];
    }
  }, {
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Query para buscar usuários com paginação
  const {
    data: paginatedData,
    isLoading,
    error,
    refetch,
  } = useQuery<{users: User[], pagination: any}>(['users-v2', currentPage, searchTerm, filterType, filterStatus], async () => {
    try {
      const params: any = {
        page: currentPage,
        limit: usersPerPage
      };

      // Adicionar filtros se existirem
      if (filterType) params.tipo_usuario = filterType;
      if (filterStatus) params.ativo = filterStatus === 'ativo';
      if (searchTerm) params.busca = searchTerm;

      const response = await UsuariosService.listar(params) as any;
      
      if (!response.data || !response.data.dados) {
        return { users: [], pagination: null };
      }

      // A API retorna os usuários diretamente em response.data.dados (array)
      // e a paginação em response.data.dados.paginacao
      const responseData = response.data.dados;
      
      // Se responseData é um array, os usuários estão diretamente nele
      let usersArray = [];
      let paginationData = null;
      
      if (Array.isArray(responseData)) {
        usersArray = responseData;
        // Buscar paginação em outro lugar da resposta se necessário
        paginationData = response.data.paginacao || null;
      } else {
        // Se não é array, pode ter estrutura com usuarios e paginacao separados
        usersArray = responseData.usuarios || [];
        paginationData = responseData.paginacao || null;
      }

      const mappedUsers = usersArray.map((user: any) => ({
        id: user.id,
        nome: user.nome,
        email: user.email,
        tipo: user.tipo_usuario,
        ativo: user.ativo,
        criadoEm: user.data_criacao?.split('T')[0] || '',
        ultimoLogin: user.ultimo_acesso?.split('T')[0] || '',
        endpoint_id: user.endpoint_id,
        prompt_ia: user.prompt_ia,
        avatar: user.dados_adicionais?.avatar || null,
      }));

      // Atualizar estados de paginação
      if (paginationData) {
        setTotalUsers(paginationData.total_registros);
        setTotalPages(paginationData.total_paginas);
      } else {
        // Se não há dados de paginação, usar o tamanho do array
        setTotalUsers(usersArray.length);
        setTotalPages(1);
      }
      
      return { 
        users: mappedUsers, 
        pagination: paginationData 
      };
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      throw error;
    }
  }, {
    staleTime: 0, // Sempre considera os dados como obsoletos
    cacheTime: 0, // Não mantém cache
    refetchOnWindowFocus: true, // Recarrega quando a janela ganha foco
    refetchOnMount: true, // Sempre recarrega ao montar o componente
    keepPreviousData: true, // Mantém dados anteriores durante carregamento
  });

  // Extrair usuários dos dados paginados
  const users = paginatedData?.users || [];
  const pagination = paginatedData?.pagination;



  // Mutation para deletar usuário
  const deleteUserMutation = useMutation(
    (userId: string) => UsuariosService.delete(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users-v2');
        showSuccess('Usuário excluído com sucesso!');
        onDeleteClose();
      },
      onError: () => {
        showError('Erro ao excluir usuário');
      },
    }
  );

  // Mutation para atualizar usuário
  const updateUserMutation = useMutation(
    (user: User) => {
      // Garantir que endpoint_id e prompt_ia sejam enviados corretamente
      const userData = {
        ...user,
        endpoint_id: user.endpoint_id || undefined,
        prompt_ia: user.prompt_ia || undefined
      };
      
      return UsuariosService.atualizar(user.id, userData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users-v2');
        showSuccess('Usuário atualizado com sucesso!');
        onEditClose();
        setEditingUser(null);
      },
      onError: (error) => {
        console.error('Erro na atualização:', error);
        showError('Erro ao atualizar usuário');
      },
    }
  );

  // Mutation para criar usuário
  const createUserMutation = useMutation(
    (user: any) => UsuariosService.criar(user),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('users-v2');
        
        // Verificar se o email foi enviado com sucesso
        if (response?.data?.dados?.emailEnviado) {
          showSuccess('Usuário criado com sucesso! Email de boas-vindas enviado.');
        } else {
          showSuccess('Usuário criado com sucesso!');
        }
        
        onCreateClose();
        setNewUser({});
      },
      onError: () => {
        showError('Erro ao criar usuário');
      },
    }
  );

  // Filtrar usuários localmente (para busca em tempo real)
  const filteredUsers = users.filter((user) => {
    const matchesSearch = !searchTerm || 
      user.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = !filterType || user.tipo === filterType;
    const matchesStatus = !filterStatus || 
      (filterStatus === 'ativo' && user.ativo) ||
      (filterStatus === 'inativo' && !user.ativo);

    return matchesSearch && matchesType && matchesStatus;
  });

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType, filterStatus]);

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    onDeleteOpen();
  };

  const handleEditUser = (user: User) => {
    console.log('=== DEBUG: handleEditUser ===');
    console.log('Usuário selecionado:', user);
    console.log('prompt_ia do usuário:', user.prompt_ia);
    
    const editingUserData = {
      ...user,
      endpoint_id: user.endpoint_id || undefined,
      prompt_ia: user.prompt_ia || ''
    };
    
    console.log('Dados do usuário para edição:', editingUserData);
    console.log('prompt_ia após mapeamento:', editingUserData.prompt_ia);
    
    setEditingUser(editingUserData);
    onEditOpen();
  };

  // Efeito para garantir que os endpoints sejam carregados quando o modal de edição for aberto
  useEffect(() => {
    if (isEditOpen && endpoints.length === 0) {
      queryClient.invalidateQueries('endpoints');
    }
  }, [isEditOpen, endpoints.length, queryClient]);

  const handleSaveUser = () => {
    if (editingUser) {
      updateUserMutation.mutate(editingUser);
    }
  };

  const handleCreateUser = () => {
    if (newUser.nome && newUser.email && newUser.senha && newUser.tipo) {
      createUserMutation.mutate({
        nome: newUser.nome,
        email: newUser.email,
        senha: newUser.senha,
        perfil: newUser.tipo, // Mapear 'tipo' para 'perfil' que o backend espera
        endpoint_id: newUser.endpoint_id || null,
        prompt_ia: newUser.prompt_ia || null,
        ativo: newUser.ativo ?? true
      });
    }
  };

  const handleInputChange = (field: keyof User, value: any) => {
    if (editingUser) {
      setEditingUser({ ...editingUser, [field]: value });
    }
  };

  const handleNewUserInputChange = (field: keyof User, value: any) => {
    setNewUser({ ...newUser, [field]: value });
  };

  const handleOpenPromptSelection = (mode: 'edit' | 'create') => {
    setPromptSelectionMode(mode);
    onPromptsOpen();
  };

  const handleSelectPrompt = (prompt: Prompt) => {
    console.log('=== DEBUG: handleSelectPrompt ===');
    console.log('Prompt selecionado:', prompt);
    console.log('prompt.conteudo:', prompt.conteudo);
    console.log('promptSelectionMode:', promptSelectionMode);
    console.log('editingUser atual:', editingUser);
    
    if (promptSelectionMode === 'edit' && editingUser) {
      const updatedUser = {
        ...editingUser,
        prompt_ia: prompt.conteudo
      };
      console.log('Atualizando editingUser com prompt_ia:', prompt.conteudo);
      console.log('editingUser atualizado:', updatedUser);
      setEditingUser(updatedUser);
    } else if (promptSelectionMode === 'create') {
      const updatedNewUser = {
        ...newUser,
        prompt_ia: prompt.conteudo
      };
      console.log('Atualizando newUser com prompt_ia:', prompt.conteudo);
      console.log('newUser atualizado:', updatedNewUser);
      setNewUser(updatedNewUser);
    }
    onPromptsClose();
  };

  const confirmDelete = () => {
    if (selectedUser) {
      deleteUserMutation.mutate(selectedUser.id);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (isLoading) {
    return <LoadingSpinner message="Carregando usuários..." />;
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text color="red.500">Erro ao carregar usuários</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Cabeçalho */}
        <Flex justify="space-between" align="center">
          <Heading size="lg">Gerenciamento de Usuários</Heading>
          <HStack spacing={3}>
            <Button
              leftIcon={<FiDownload />}
              variant="outline"
              size="sm"
            >
              Exportar
            </Button>
            <Button
               leftIcon={<FiRefreshCw />}
               variant="outline"
               size="sm"
               onClick={() => refetch()}
               isLoading={isLoading}
             >
               Atualizar
             </Button>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="medicare"
              size="sm"
              onClick={() => {
                setNewUser({}); // Limpar o estado antes de abrir o modal
                onCreateOpen();
              }}
            >
              Novo Usuário
            </Button>
          </HStack>
        </Flex>

        {/* Filtros */}
        <Card>
          <CardBody>
            <HStack spacing={4} wrap="wrap">
              <InputGroup maxW="300px">
                <InputLeftElement>
                  <FiSearch color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar por nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              
              <Select
                placeholder="Todos os tipos"
                maxW="200px"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="admin">Administrador</option>
                <option value="medico">Médico</option>
              </Select>
              
              <Select
                placeholder="Todos os status"
                maxW="200px"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </Select>
              
              <Button
                leftIcon={<FiFilter />}
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('');
                  setFilterStatus('');
                }}
              >
                Limpar
              </Button>
            </HStack>
          </CardBody>
        </Card>

        {/* Tabela de usuários */}
        <Card>
          <CardHeader>
            <Flex justify="space-between" align="center">
              <Text fontSize="md" fontWeight="medium">
                {totalUsers} usuário(s) encontrado(s)
              </Text>
              <Text fontSize="sm" color="gray.500">
                Página {currentPage} de {totalPages || 1}
              </Text>
            </Flex>
          </CardHeader>
          <CardBody>
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Usuário</Th>
                    <Th>Email</Th>
                    <Th>Tipo</Th>
                    <Th>Status</Th>
                    <Th>Criado em</Th>
                    <Th>Último login</Th>
                    <Th>Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredUsers.map((user) => (
                    <Tr key={user.id}>
                      <Td>
                        <HStack spacing={3}>
                          <Avatar
                            size="sm"
                            name={user.nome}
                            src={getAvatarUrl(user.avatar)}
                          />
                          <Text fontWeight="medium">{user.nome}</Text>
                        </HStack>
                      </Td>
                      <Td>{user.email}</Td>
                      <Td>
                        <Badge
                          colorScheme={USER_TYPES[user.tipo].color}
                          variant="subtle"
                        >
                          {USER_TYPES[user.tipo].label}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={user.ativo ? 'green' : 'red'}
                          variant="subtle"
                        >
                          {user.ativo ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </Td>
                      <Td>{formatDate(user.criadoEm)}</Td>
                      <Td>
                        {user.ultimoLogin ? formatDate(user.ultimoLogin) : 'Nunca'}
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <Tooltip label="Visualizar">
                            <IconButton
                              aria-label="Visualizar usuário"
                              icon={<FiEye />}
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditUser(user)}
                            />
                          </Tooltip>
                          <Tooltip label="Editar">
                            <IconButton
                              aria-label="Editar usuário"
                              icon={<FiEdit2 />}
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditUser(user)}
                            />
                          </Tooltip>
                          <Tooltip label="Excluir">
                            <IconButton
                              aria-label="Excluir usuário"
                              icon={<FiTrash2 />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => handleDeleteUser(user)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            {/* Controles de Paginação */}
            {totalPages > 1 && (
              <Flex justify="space-between" align="center" mt={4} pt={4} borderTop="1px" borderColor="gray.200">
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentPage(1)}
                    isDisabled={currentPage === 1}
                  >
                    Primeira
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    isDisabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                </HStack>

                <HStack spacing={1}>
                  {/* Mostrar páginas próximas */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        variant={currentPage === pageNum ? "solid" : "outline"}
                        colorScheme={currentPage === pageNum ? "medicare" : "gray"}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </HStack>

                <HStack spacing={2}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    isDisabled={currentPage === totalPages}
                  >
                    Próxima
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentPage(totalPages)}
                    isDisabled={currentPage === totalPages}
                  >
                    Última
                  </Button>
                </HStack>
              </Flex>
            )}

            {/* Informações de paginação */}
            <Text fontSize="sm" color="gray.500" textAlign="center" mt={2}>
              {totalUsers > 0 ? (
                `Mostrando ${((currentPage - 1) * usersPerPage) + 1} a ${Math.min(currentPage * usersPerPage, totalUsers)} de ${totalUsers} usuários`
              ) : (
                'Nenhum usuário encontrado'
              )}
            </Text>
          </CardBody>
        </Card>
      </VStack>

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Excluir Usuário
            </AlertDialogHeader>

            <AlertDialogBody>
              Tem certeza que deseja excluir o usuário{' '}
              <strong>{selectedUser?.nome}</strong>? Esta ação não pode ser desfeita.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                ml={3}
                isLoading={deleteUserMutation.isLoading}
              >
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Modal de edição de usuário */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar Usuário</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editingUser && (
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Nome</FormLabel>
                  <Input
                    value={editingUser.nome}
                    onChange={(e) => handleInputChange('nome', e.target.value)}
                    placeholder="Nome completo"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    value={editingUser.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="email@exemplo.com"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Tipo de Usuário</FormLabel>
                  <Select
                    value={editingUser.tipo}
                    onChange={(e) => handleInputChange('tipo', e.target.value)}
                  >
                    <option value="admin">Administrador</option>
                    <option value="medico">Médico</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Endpoint</FormLabel>
                  <Select
                    placeholder={isLoadingEndpoints ? "Carregando endpoints..." : "Selecione um endpoint"}
                    value={editingUser.endpoint_id || ''}
                    onChange={(e) => handleInputChange('endpoint_id', e.target.value === '' ? null : e.target.value)}
                    isDisabled={isLoadingEndpoints}
                  >
                    <option value="">Nenhum endpoint</option>
                    {endpoints && endpoints.length > 0 ? (
                      endpoints.map((endpoint) => (
                        <option key={endpoint.id} value={endpoint.id}>
                          {endpoint.nome}
                        </option>
                      ))
                    ) : (
                      !isLoadingEndpoints && (
                        <option value="" disabled>
                          Nenhum endpoint disponível
                        </option>
                      )
                    )}
                  </Select>
                  {editingUser.endpoint_id && (
                    <Text fontSize="sm" color="blue.500" mt={1}>
                      Endpoint selecionado: {endpoints?.find(e => e.id === editingUser.endpoint_id)?.nome || 'Carregando...'}
                    </Text>
                  )}
                </FormControl>

                <FormControl>
                  <Flex justifyContent="space-between" alignItems="center" mb={2}>
                    <FormLabel mb={0}>Prompt IA</FormLabel>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiList />}
                      onClick={() => handleOpenPromptSelection('edit')}
                      isLoading={isLoadingPrompts}
                    >
                      Selecionar Prompt
                    </Button>
                  </Flex>
                  <Textarea
                    rows={4}
                    value={editingUser.prompt_ia || ''}
                    onChange={(e) => handleInputChange('prompt_ia', e.target.value)}
                    placeholder="Digite o prompt personalizado para IA (opcional)..."
                    maxLength={5000}
                    resize="vertical"
                    borderColor={editingUser.prompt_ia ? "blue.300" : "gray.200"}
                    _hover={{ borderColor: editingUser.prompt_ia ? "blue.400" : "gray.300" }}
                  />
                  <Flex justifyContent="space-between" alignItems="center" mt={1}>
                    <Text fontSize="sm" color="gray.500">
                      {(editingUser.prompt_ia || '').length}/5000 caracteres
                    </Text>
                    {editingUser.prompt_ia && editingUser.prompt_ia.length > 0 && (
                      <Badge colorScheme="blue" variant="subtle" px={2} py={1}>
                        ✓ Prompt personalizado configurado
                      </Badge>
                    )}
                  </Flex>
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="user-status" mb="0">
                    Usuário Ativo
                  </FormLabel>
                  <Switch
                    id="user-status"
                    isChecked={editingUser.ativo}
                    onChange={(e) => handleInputChange('ativo', e.target.checked)}
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveUser}
              isLoading={updateUserMutation.isLoading}
            >
              Salvar Alterações
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de criação de usuário */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Criar Novo Usuário</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nome</FormLabel>
                <Input
                  value={newUser.nome || ''}
                  onChange={(e) => handleNewUserInputChange('nome', e.target.value)}
                  placeholder="Nome completo"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={newUser.email || ''}
                  onChange={(e) => handleNewUserInputChange('email', e.target.value)}
                  placeholder="email@exemplo.com"
                  autoComplete="off"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Senha</FormLabel>
                <Input
                  type="password"
                  value={newUser.senha || ''}
                  onChange={(e) => handleNewUserInputChange('senha', e.target.value)}
                  placeholder="Digite a senha (mín. 8 caracteres, maiúscula, minúscula e número)"
                  minLength={8}
                  borderColor={newUser.senha && !isValidPassword(newUser.senha) ? 'red.300' : undefined}
                  autoComplete="new-password"
                />
                <Text 
                  fontSize="sm" 
                  color={newUser.senha ? (isValidPassword(newUser.senha) ? 'green.500' : 'red.500') : 'gray.500'} 
                  mt={1}
                >
                  {newUser.senha ? 
                    (isValidPassword(newUser.senha) ? '✓ Senha válida' : '✗ Senha deve ter 8+ caracteres, maiúscula, minúscula e número') :
                    'Mínimo de 8 caracteres com maiúscula, minúscula e número'
                  }
                </Text>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Tipo de Usuário</FormLabel>
                <Select
                  placeholder="Selecione o tipo"
                  value={newUser.tipo || ''}
                  onChange={(e) => handleNewUserInputChange('tipo', e.target.value)}
                >
                  <option value="admin">Administrador</option>
                  <option value="medico">Médico</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Endpoint</FormLabel>
                <Select
                  placeholder={isLoadingEndpoints ? "Carregando endpoints..." : "Selecione um endpoint"}
                  value={newUser.endpoint_id || ''}
                  onChange={(e) => handleNewUserInputChange('endpoint_id', e.target.value || null)}
                  isDisabled={isLoadingEndpoints}
                >
                  {endpoints && endpoints.length > 0 ? (
                    endpoints.map((endpoint) => (
                      <option key={endpoint.id} value={endpoint.id}>
                        {endpoint.nome}
                      </option>
                    ))
                  ) : (
                    !isLoadingEndpoints && (
                      <option value="" disabled>
                        Nenhum endpoint disponível
                      </option>
                    )
                  )}
                </Select>
              </FormControl>

              <FormControl>
                <Flex justifyContent="space-between" alignItems="center" mb={2}>
                  <FormLabel mb={0}>Prompt IA</FormLabel>
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<FiList />}
                    onClick={() => handleOpenPromptSelection('create')}
                    isLoading={isLoadingPrompts}
                  >
                    Selecionar Prompt
                  </Button>
                </Flex>
                <Textarea
                  rows={4}
                  value={newUser.prompt_ia || ''}
                  onChange={(e) => handleNewUserInputChange('prompt_ia', e.target.value)}
                  placeholder="Digite o prompt personalizado para IA (opcional)..."
                  maxLength={5000}
                  resize="vertical"
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  {(newUser.prompt_ia || '').length}/5000 caracteres
                </Text>
                {newUser.prompt_ia && newUser.prompt_ia.length > 0 && (
                  <Text fontSize="xs" color="blue.500" mt={1}>
                    ✓ Prompt personalizado configurado
                  </Text>
                )}
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="new-user-status" mb="0">
                  Usuário Ativo
                </FormLabel>
                <Switch
                  id="new-user-status"
                  isChecked={newUser.ativo ?? true}
                  onChange={(e) => handleNewUserInputChange('ativo', e.target.checked)}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateUser}
              isLoading={createUserMutation.isLoading}
              isDisabled={!newUser.nome || !newUser.email || !newUser.senha || !newUser.tipo || !isValidPassword(newUser.senha)}
            >
              Criar Usuário
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de Seleção de Prompts */}
      <Modal isOpen={isPromptsOpen} onClose={onPromptsClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Selecionar Prompt Padrão
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isLoadingPrompts ? (
              <Flex justify="center" py={8}>
                <Spinner size="lg" />
              </Flex>
            ) : prompts.length === 0 ? (
              <Text textAlign="center" py={8} color="gray.500">
                Nenhum prompt disponível
              </Text>
            ) : (
              <VStack spacing={3} align="stretch">
                {prompts.map((prompt) => (
                  <Card
                    key={prompt.id}
                    cursor="pointer"
                    _hover={{ bg: 'gray.50' }}
                    onClick={() => handleSelectPrompt(prompt)}
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <CardBody py={3}>
                      <Flex justify="space-between" align="start">
                        <VStack align="start" spacing={1} flex={1}>
                          <Text fontWeight="medium" fontSize="sm">
                            {prompt.nome}
                          </Text>
                          {prompt.categoria && (
                            <Badge size="sm" colorScheme="blue" variant="subtle">
                              {prompt.categoria}
                            </Badge>
                          )}
                          {prompt.conteudo && (
                            <Text fontSize="xs" color="gray.600" noOfLines={2}>
                              {prompt.conteudo}
                            </Text>
                          )}
                        </VStack>
                        <Button size="xs" colorScheme="blue" variant="ghost">
                          Selecionar
                        </Button>
                      </Flex>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onPromptsClose}>
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default UserManagementPage;